import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";

/**
 * Fase 12 · Módulo C — SLA progress bar for support tickets.
 *
 * Props:
 *   - createdAt (ISO string) · required
 *   - firstResponseAt / resolvedAt (ISO or null)
 *   - firstDeadlineAt / resolutionDeadlineAt (ISO)
 *   - firstResponseHours / resolutionHours (number)
 *   - status (string)
 *   - compact (bool) · used inside table rows
 *
 * Colors: green (< 60% window used) → amber (60-90%) → red (>= 90% or breached).
 * Frozen when target already met (first_response_at set OR resolved_at set).
 */
function _diffPct(start, deadline, now = Date.now()) {
  const s = Date.parse(start);
  const d = Date.parse(deadline);
  if (Number.isNaN(s) || Number.isNaN(d) || d <= s) return 100;
  const pct = ((now - s) / (d - s)) * 100;
  return Math.max(0, Math.min(100, pct));
}

function _humanLeft(deadline, now = Date.now()) {
  const d = Date.parse(deadline);
  if (Number.isNaN(d)) return "";
  const diff = d - now;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const sign = diff < 0 ? "vencido hace " : "en ";
  return `${sign}${h > 0 ? `${h}h ` : ""}${m}m`;
}

function _color(pct, met) {
  if (met) return { bar: "bg-emerald-500", text: "text-emerald-300", cls: "border-emerald-500/30 bg-emerald-500/5" };
  if (pct >= 100) return { bar: "bg-red-500", text: "text-red-300", cls: "border-red-500/40 bg-red-500/10" };
  if (pct >= 90) return { bar: "bg-red-500", text: "text-red-300", cls: "border-red-500/30 bg-red-500/5" };
  if (pct >= 60) return { bar: "bg-amber-500", text: "text-amber-300", cls: "border-amber-500/30 bg-amber-500/5" };
  return { bar: "bg-emerald-500", text: "text-emerald-300", cls: "border-emerald-500/30 bg-emerald-500/5" };
}

function Row({ label, pct, met, deadline, hours, kind, testid }) {
  const c = _color(pct, met);
  const icon = met ? <CheckCircle2 className="w-3 h-3" /> :
    pct >= 90 ? <AlertTriangle className="w-3 h-3" /> :
    <Clock className="w-3 h-3" />;
  return (
    <div data-testid={testid} className="space-y-1">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
        <span className="text-gray-400 font-semibold inline-flex items-center gap-1">
          {icon} {label}
        </span>
        <span className={`font-bold ${c.text}`}>
          {met ? "cumplido" : `${Math.round(pct)}% · ${_humanLeft(deadline)}`}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full ${c.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] text-gray-500">
        Objetivo {hours}h — {kind}. {met ? "Meta alcanzada." : `Deadline ${new Date(deadline).toLocaleString("es-MX")}`}
      </p>
    </div>
  );
}

export default function SlaProgressBar({ ticket, compact = false }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  if (!ticket?.created_at) return null;
  const isClosed = ticket.status === "closed";
  const firstMet = !!ticket.first_response_at;
  const resolMet = !!ticket.resolved_at || isClosed;
  const firstPct = firstMet
    ? _diffPct(ticket.created_at, ticket.first_response_at, Date.parse(ticket.first_response_at))
    : _diffPct(ticket.created_at, ticket.sla_first_deadline_at, now);
  const resolPct = resolMet
    ? _diffPct(ticket.created_at, ticket.resolved_at || ticket.sla_resolution_deadline_at,
               Date.parse(ticket.resolved_at || ticket.sla_resolution_deadline_at))
    : _diffPct(ticket.created_at, ticket.sla_resolution_deadline_at, now);

  if (compact) {
    // one-line horizontal (staff table row)
    const c = _color(Math.max(firstPct, resolPct), firstMet && resolMet);
    const worse = Math.max(firstMet ? 0 : firstPct, resolMet ? 0 : resolPct);
    return (
      <div data-testid="sla-progress-compact" className="w-full min-w-[80px]">
        <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden">
          <div className={`h-full ${c.bar}`} style={{ width: `${worse}%` }} />
        </div>
        <p className={`mt-0.5 text-[10px] font-bold ${c.text}`}>
          {firstMet && resolMet ? "cumplido" :
            worse >= 100 ? "vencido" :
            worse >= 90 ? `crítico ${Math.round(worse)}%` :
            `${Math.round(worse)}%`}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="sla-progress-bar" className={`rounded-2xl border ${_color(Math.max(firstPct, resolPct), firstMet && resolMet).cls} p-3 space-y-3`}>
      <Row
        label="Respuesta"
        pct={firstPct}
        met={firstMet}
        deadline={ticket.sla_first_deadline_at}
        hours={ticket.sla_first_response_hours}
        kind={`prioridad ${ticket.priority}`}
        testid="sla-first-response"
      />
      <Row
        label="Resolución"
        pct={resolPct}
        met={resolMet}
        deadline={ticket.sla_resolution_deadline_at}
        hours={ticket.sla_resolution_hours}
        kind={`prioridad ${ticket.priority}`}
        testid="sla-resolution"
      />
    </div>
  );
}
